import React, { useState, useEffect } from 'react';
import { useAuth, useAuthPrompt } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  BarChart3,
  Download,
  FileText,
  Loader2,
  TrendingUp,
  TrendingDown,
  PieChart,
  Calendar,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  List
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const reportTypes = [
  { id: 'expense-summary', label: 'Expense Summary', icon: TrendingDown, description: 'All expenses by category' },
  { id: 'income-summary', label: 'Income Summary', icon: TrendingUp, description: 'All income by source' },
  { id: 'monthly-overview', label: 'Monthly Overview', icon: Calendar, description: 'Month-by-month breakdown' },
  { id: 'category-breakdown', label: 'By Category', icon: PieChart, description: 'Spending breakdown by category' },
];

const periodOptions = [
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
];

export default function PersonalReports() {
  const { isAuthenticated } = useAuth();
  const { showAuthPrompt } = useAuthPrompt();
  
  const [selectedReport, setSelectedReport] = useState('expense-summary');
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    generateReport();
  }, [selectedReport, period]);

  const generateReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.get(`${API_URL}/api/personal/reports/${selectedReport}`, {
        params: { period },
        headers
      });
      setReportData(res.data);
    } catch (error) {
      // Demo data based on report type
      if (selectedReport === 'expense-summary') {
        setReportData({
          total: 1850,
          count: 24,
          categories: [
            { name: 'Food & Dining', amount: 425, percentage: 23 },
            { name: 'Shopping', amount: 380, percentage: 21 },
            { name: 'Transportation', amount: 290, percentage: 16 },
            { name: 'Entertainment', amount: 220, percentage: 12 },
            { name: 'Utilities', amount: 185, percentage: 10 },
            { name: 'Other', amount: 350, percentage: 19 }
          ]
        });
      } else if (selectedReport === 'income-summary') {
        setReportData({
          total: 3500,
          count: 4,
          sources: [
            { name: 'Salary', amount: 3000, percentage: 86 },
            { name: 'Freelance', amount: 350, percentage: 10 },
            { name: 'Other', amount: 150, percentage: 4 }
          ]
        });
      } else if (selectedReport === 'monthly-overview') {
        setReportData({
          months: [
            { month: 'Jan', income: 3200, expenses: 1650, net: 1550 },
            { month: 'Feb', income: 3500, expenses: 1850, net: 1650 },
            { month: 'Mar', income: 3100, expenses: 1720, net: 1380 }
          ],
          totals: { income: 9800, expenses: 5220, net: 4580 }
        });
      } else if (selectedReport === 'category-breakdown') {
        setReportData({
          categories: [
            { name: 'Food & Dining', amount: 425, color: 'hsl(38 90% 55%)' },
            { name: 'Shopping', amount: 380, color: 'hsl(280 60% 55%)' },
            { name: 'Transportation', amount: 290, color: 'hsl(200 80% 55%)' },
            { name: 'Entertainment', amount: 220, color: 'hsl(340 80% 55%)' },
            { name: 'Utilities', amount: 185, color: 'hsl(160 60% 50%)' },
            { name: 'Other', amount: 350, color: 'hsl(0 0% 50%)' }
          ],
          total: 1850
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!isAuthenticated) {
      showAuthPrompt('Export Report');
      return;
    }
    toast.success('Report exported as CSV');
  };

  const periodLabel = periodOptions.find(p => p.value === period)?.label || 'This Month';

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="personal-reports">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk']">
            Personal <span className="text-[hsl(var(--accent))]">Reports</span>
          </h1>
          <p className="text-muted-foreground mt-1">View summaries of your personal finances</p>
        </div>
        <Button onClick={handleExport} variant="outline">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Report Type Selection */}
        <div className="flex flex-wrap gap-2">
          {reportTypes.map((report) => {
            const Icon = report.icon;
            const isSelected = selectedReport === report.id;
            return (
              <button
                key={report.id}
                onClick={() => setSelectedReport(report.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                  isSelected 
                    ? 'bg-[hsl(var(--accent))] text-black' 
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="w-4 h-4" />
                {report.label}
              </button>
            );
          })}
        </div>
        
        {/* Period Selection */}
        <div className="flex bg-muted rounded-lg p-1 h-fit">
          {periodOptions.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                period === p.value 
                  ? 'bg-background text-foreground shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--accent))]" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Expense Summary Report */}
          {selectedReport === 'expense-summary' && reportData && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="card-premium">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Expenses</p>
                        <p className="text-3xl font-bold text-red-500">{formatCurrency(reportData.total)}</p>
                        <p className="text-xs text-muted-foreground mt-1">{periodLabel}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-red-500/10">
                        <TrendingDown className="w-8 h-8 text-red-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="card-premium">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Transactions</p>
                        <p className="text-3xl font-bold">{reportData.count}</p>
                        <p className="text-xs text-muted-foreground mt-1">{periodLabel}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-[hsl(var(--accent))]/10">
                        <List className="w-8 h-8 text-[hsl(var(--accent))]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <Card className="card-premium">
                <CardHeader>
                  <CardTitle>Expenses by Category</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {reportData.categories?.map((cat, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{cat.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">{cat.percentage}%</span>
                          <span className="font-semibold">{formatCurrency(cat.amount)}</span>
                        </div>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-red-500 rounded-full transition-all"
                          style={{ width: `${cat.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </>
          )}

          {/* Income Summary Report */}
          {selectedReport === 'income-summary' && reportData && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="card-premium">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Income</p>
                        <p className="text-3xl font-bold text-emerald-500">{formatCurrency(reportData.total)}</p>
                        <p className="text-xs text-muted-foreground mt-1">{periodLabel}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-emerald-500/10">
                        <TrendingUp className="w-8 h-8 text-emerald-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="card-premium">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Income Sources</p>
                        <p className="text-3xl font-bold">{reportData.count}</p>
                        <p className="text-xs text-muted-foreground mt-1">{periodLabel}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-[hsl(var(--accent))]/10">
                        <List className="w-8 h-8 text-[hsl(var(--accent))]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <Card className="card-premium">
                <CardHeader>
                  <CardTitle>Income by Source</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {reportData.sources?.map((src, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{src.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">{src.percentage}%</span>
                          <span className="font-semibold">{formatCurrency(src.amount)}</span>
                        </div>
                      </div>
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-emerald-500 rounded-full transition-all"
                          style={{ width: `${src.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </>
          )}

          {/* Monthly Overview Report */}
          {selectedReport === 'monthly-overview' && reportData && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="card-premium">
                  <CardContent className="p-5">
                    <p className="text-sm text-muted-foreground">Total Income</p>
                    <p className="text-2xl font-bold text-emerald-500">{formatCurrency(reportData.totals?.income)}</p>
                  </CardContent>
                </Card>
                <Card className="card-premium">
                  <CardContent className="p-5">
                    <p className="text-sm text-muted-foreground">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-500">{formatCurrency(reportData.totals?.expenses)}</p>
                  </CardContent>
                </Card>
                <Card className="card-premium">
                  <CardContent className="p-5">
                    <p className="text-sm text-muted-foreground">Net Savings</p>
                    <p className={`text-2xl font-bold ${reportData.totals?.net >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {formatCurrency(reportData.totals?.net)}
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              <Card className="card-premium">
                <CardHeader>
                  <CardTitle>Monthly Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 font-medium">Month</th>
                          <th className="text-right py-3 px-4 font-medium">Income</th>
                          <th className="text-right py-3 px-4 font-medium">Expenses</th>
                          <th className="text-right py-3 px-4 font-medium">Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.months?.map((month, idx) => (
                          <tr key={idx} className="border-b border-border last:border-0">
                            <td className="py-3 px-4 font-medium">{month.month}</td>
                            <td className="py-3 px-4 text-right text-emerald-500">{formatCurrency(month.income)}</td>
                            <td className="py-3 px-4 text-right text-red-500">{formatCurrency(month.expenses)}</td>
                            <td className={`py-3 px-4 text-right font-semibold ${month.net >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                              {formatCurrency(month.net)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Category Breakdown Report */}
          {selectedReport === 'category-breakdown' && reportData && (
            <>
              <Card className="card-premium">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Spending</p>
                      <p className="text-3xl font-bold">{formatCurrency(reportData.total)}</p>
                      <p className="text-xs text-muted-foreground mt-1">{periodLabel}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-[hsl(var(--accent))]/10">
                      <PieChart className="w-8 h-8 text-[hsl(var(--accent))]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {reportData.categories?.map((cat, idx) => {
                  const percentage = reportData.total > 0 ? Math.round((cat.amount / reportData.total) * 100) : 0;
                  return (
                    <Card key={idx} className="card-premium">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: cat.color }}
                          />
                          <span className="font-medium">{cat.name}</span>
                        </div>
                        <div className="flex items-end justify-between">
                          <p className="text-2xl font-bold">{formatCurrency(cat.amount)}</p>
                          <Badge variant="outline">{percentage}%</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
